import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import Loader from "../../../../components/template/loader.jsx";
import AttendanceService from "../../../../services/dispatch/attendance-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;
const API_URL = import.meta.env.VITE_API_URL ?? "";

const CloseAttendanceModal = ({payload, onClose, onSuccess}) => {

    const navigate = navigateHook();

    const [nature, setNature] = useState("");
    const [statusName, setStatusName] = useState("");
    const [observationFinal, setObservationFinal] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requested = Boolean(payload);
    const attendance = payload?.attendance;
    const attendanceId = attendance?.attendanceId;

    const dispatchQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.attendanceOpenDispatch(attendanceId),
        queryFn: () => AttendanceService.verifyAttendanceHasDispatchOpened(attendanceId),
        enabled: requested && Boolean(attendanceId),
    });

    const closeQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.attendanceClose(attendanceId),
        queryFn: () => AttendanceService.loadCloseAttendance(attendanceId),
        enabled: requested && Boolean(attendanceId) && dispatchQuery.isSuccess && !dispatchQuery.data?.garrison,
    });
    const isReady = !dispatchQuery.isFetching && (
        dispatchQuery.isError
        || (
            dispatchQuery.isSuccess
            && !dispatchQuery.data?.garrison
            && !closeQuery.isFetching
            && (closeQuery.isSuccess || closeQuery.isError)
        )
    );
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;
        setNature("");
        setStatusName("");
        setObservationFinal("");
        setIsSubmitting(false);
    }, [requested]);

    useEffect(() => {
        if (!requested || !dispatchQuery.isSuccess || !dispatchQuery.data?.garrison) return;
        projectUtils.swalErrorMessage(
            "Não é possível encerrar o atendimento!",
            `<h3>Existe um despacho em aberto:</h3>${dispatchQuery.data.garrison.name ?? ""}`,
            "Ok",
            false,
            "Não",
            undefined,
            true
        );
        closeModal();
    }, [closeModal, dispatchQuery.data, dispatchQuery.isSuccess, requested]);

    useEffect(() => {
        if (!closeQuery.data) return;
        setNature(closeQuery.data.nature ?? "");
        setObservationFinal(closeQuery.data.observationFinal ?? "");
    }, [closeQuery.data]);

    const natureOptions = useMemo(() => {
        const options = (closeQuery.data?.listNature ?? []).map(item => ({
            value: item.name ?? item.alias,
            label: item.alias ?? item.name,
        }));

        if (closeQuery.data?.nature && !options.some(option => option.value === closeQuery.data.nature)) {
            return [...options, {value: closeQuery.data.nature, label: closeQuery.data.nature}];
        }

        return options;
    }, [closeQuery.data]);

    const statusOptions = useMemo(() => (closeQuery.data?.listStatus ?? []).map(item => ({
        value: item,
        label: item,
    })), [closeQuery.data?.listStatus]);

    const isLoading = dispatchQuery.isLoading || closeQuery.isLoading;
    const hasError = dispatchQuery.isError || closeQuery.isError;
    const isTranscriptionMissing = closeQuery.data?.transcriptionIsNull;

    const handleSubmit = async () => {
        if (!nature) {
            projectUtils.swalErrorMessage("", "Nenhuma natureza selecionada", "Ok");
            return;
        }

        if (!statusName) {
            projectUtils.swalErrorMessage("", "Nenhum status selecionado", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await AttendanceService.closeAttendanceByStatus({attendanceId, statusName, nature, observationFinal});
            if (result === "BAD_REQUEST" || result === "") {
                projectUtils.swalErrorMessage("", "Não foi possível encerrar o atendimento", "Ok");
                setIsSubmitting(false);
                return;
            }

            if (isTranscriptionMissing) {
                projectUtils.swalMessage(
                    "warning",
                    "Transcrição não informada",
                    "A transcrição do atendimento está vazia. Deseja informá-la agora?",
                    "Sim",
                    true,
                    "Não",
                    ({isConfirmed}) => {
                        if (isConfirmed) edit();
                    }
                );
            } else {
                projectUtils.swalSuccessMessage("success", "Sucesso", "Atendimento encerrado com sucesso", "Ok");
            }

            onSuccess();
        } catch (error) {
            console.error("Erro ao encerrar atendimento pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível encerrar o atendimento";
            projectUtils.swalErrorMessage("", message, "Ok");
            setIsSubmitting(false);
        }
    };

    const edit = () => {
        const url = `${DESPACHO_WEB_URL}/attendance/form/${attendanceId}?redir=despacho/dashboard&focus=transcription`
        navigate(url, true);
    }

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title="Encerrar atendimento" className="w-full max-w-2xl" isCloseClick={false} animated={false}>
                <Modal.Body className="relative max-h-[calc(100vh-180px)] flex flex-col gap-4 overflow-auto">
                    {hasError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar os dados do atendimento.</div>}
                    {!hasError && !dispatchQuery.data?.garrison && (
                        <>
                            <SelectComponent classNameWrapper="w-full" label="Natureza *" value={nature} options={natureOptions} onChange={selected => setNature(selected?.value ?? "")}/>
                            <SelectComponent classNameWrapper="w-full" label="Status *" value={statusName} options={statusOptions} onChange={selected => setStatusName(selected?.value ?? "")}/>
                            <InputTextarea label="Observação do atendente" value={closeQuery.data?.observation ?? ""} disabled height={120}/>
                            <InputTextarea label="Observação final" value={observationFinal} onChange={({target}) => setObservationFinal(target.value)} height={120}/>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" onClick={handleSubmit} loading={isSubmitting} disabled={hasError || isLoading || Boolean(dispatchQuery.data?.garrison)} actionFeedbackType='save'>Salvar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CloseAttendanceModal;